import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { SyntheticEvent, useState, useRef } from "react";
import { useSelector } from "react-redux";
import * as driverActivitiesActions from "../../../ducks/app/driver-activities";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { DateTime } from "luxon";
import { useAppDispatch } from "../../../redux-store";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import { selectStopWeightsFromActivities } from "../../../ducks/data/route-with-stops/selectors";
import { StopNew } from "../../../ducks/data/route-with-stops/types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import useFeature from "../../../hooks/useFeature";
import Decimal from "decimal.js-light";
import { useParams } from "../hooks";
import LoadingButton from "../../../components/LoadingButton";
import { parseTrailerIdParam } from "../../../routes";

const formatWeight = (input: string | null) => input && `${input} kg`;

const isValidWeight = (value: string): boolean => {
  if (!value || value.trim() === "") return false;
  try {
    new Decimal(value);
    return true;
  } catch {
    return false;
  }
};

const AddWeightModal = (props: { stop: StopNew; onClose: () => void }) => {
  const { stop, onClose } = props;

  const { t } = useTranslation(["stopPage", "components"]);
  const [weight, setWeight] = useState("");
  const [loading, setLoading] = useState(false);
  const [weightIdForEdit, setWeightIdForEdit] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const weights = useSelector(selectStopWeightsFromActivities(stop.id));
  const params = useParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const routeId = (params.routeId as string) || "";
  const trailerId = (params.truckId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);

  const onSaveClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.editWeight({
          routeId,
          id: weightIdForEdit!,
          weightInKg: new Decimal(weight),
          cargoId: stop.cargoId,
          stopId: stop.id,
          trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      setWeightIdForEdit(null);
      setWeight("");
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  const onEditClick = async (weightId: string) => {
    const w = weights.find((w) => w.id === weightId);
    if (w) {
      setWeightIdForEdit(weightId);
      setWeight(w.weight.toString());
      inputRef.current?.focus();
    }
  };

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (weightIdForEdit) {
      onSaveClick();
    } else {
      onAddClick();
    }
  };

  const onAddClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.addWeight({
          routeId,
          weightInKg: new Decimal(weight),
          cargoId: stop.cargoId,
          stopId: stop.id,
          truckId: trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <div className="px-8 py-4">
        <Typography variant="h6" component="h2" pt={2}>
          {t("weight.heading")}
        </Typography>
        <Typography variant="body1" component="div">
          <p>
            {t("weight.plannedLabel") + " "}
            {formatWeight(stop.weight) || t("weight.unspecified")}
          </p>
          <p>
            {t("weight.actualLabel") + " "}
            {formatWeight(stop.measuredWeight) || t("weight.unspecified")}
          </p>
        </Typography>
        <List>
          {weights.map((w, i) => (
            <WeightListItem
              key={i}
              w={{
                ...w,
                weight: isValidWeight(String(w.weight))
                  ? new Decimal(w.weight)
                  : new Decimal(0),
              }}
              routeId={routeId}
              cargoId={stop.cargoId}
              stopId={stop.id}
              trailerId={trailerId}
              unpoweredTrailerId={unpoweredTrailerId}
              onEditClick={onEditClick}
            />
          ))}
        </List>
        <form onSubmit={onSubmit} className="pt-4">
          <Grid container spacing={2} direction="column">
            <Grid columns={12}>
              <TextField
                autoFocus
                fullWidth
                type="number"
                inputRef={inputRef}
                value={weight}
                label={t("stopPage:weight.textFieldLabel")}
                placeholder="1000"
                onChange={(e) => {
                  setWeight(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("stopPage:weight.textFieldAdornment")}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid columns={12}>
              <div className="flex gap-2 justify-between w-100 mt-4">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={onClose}
                >
                  {t("stopPage:weight.backButtonLabel")}
                </Button>
                <LoadingButton
                  loading={loading}
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  disabled={!isValidWeight(weight)}
                >
                  <span>
                    {weightIdForEdit
                      ? t("components:buttonLabels.save")
                      : t("stopPage:weight.addButtonLabel")}
                  </span>
                </LoadingButton>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </Dialog>
  );
};

const WeightListItem = (props: {
  w: { createdAt: string; weight: Decimal; id: string };
  cargoId: string;
  stopId: string;
  routeId: string;
  trailerId: string;
  unpoweredTrailerId: string | null;
  onEditClick: (weightId: string) => void;
}) => {
  const { w, cargoId, stopId, routeId, trailerId, unpoweredTrailerId, onEditClick } = props;
  const { t } = useTranslation(["stopPage", "components"]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const dispatch = useAppDispatch();
  const isEditDeleteEnabled = useFeature("driver-app-edit-delete-weights");

  const onConfirmDeleteClick = async () => {
    setRequestInProgress(true);
    dispatch(
      driverActivitiesActions.deleteWeight({
        id: w.id,
        cargoId,
        routeId,
        stopId,
        truckId: trailerId,
        unpoweredTrailerId,
      })
    )
      .unwrap()
      .then(() => {
        setShowConfirmDeleteDialog(false);
      })
      .catch(() => {
        // Already reported and shown by the thunk.
      })
      .finally(() => setRequestInProgress(false));
  };

  return (
    <>
      <Dialog
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2" pt={2}>
            {t("stopPage:weight.deleteConfirmTitle")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mt={2}>
            {t("stopPage:weight.deleteConfirmMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-between w-100 mt-4">
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={requestInProgress}
              onClick={() => setShowConfirmDeleteDialog(false)}
            >
              {t("components:buttonLabels.cancel")}
            </Button>
            <LoadingButton
              type="button"
              fullWidth
              size="large"
              variant="contained"
              color="error"
              disabled={requestInProgress}
              onClick={onConfirmDeleteClick}
            >
              {t("components:buttonLabels.delete")}
            </LoadingButton>
          </div>
        </DialogActions>
      </Dialog>
      <ListItem sx={{ pl: 0 }} key={w.createdAt} disableGutters divider>
        <div className="flex flex-col justify-between w-full">
          <div className="flex justify-between flex-grow items-center">
            <div style={{ color: "var(--gray-500)", fontSize: "0.9rem" }}>
              {DateTime.fromISO(w.createdAt).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </div>
            {isEditDeleteEnabled && (
              <div className="flex items-center">
                <IconButton onClick={() => onEditClick(w.id)}>
                  <EditIcon style={{ color: "var(--gray-500)" }} />
                </IconButton>
                <IconButton
                  style={{ paddingRight: 0 }}
                  onClick={() => setShowConfirmDeleteDialog(true)}
                >
                  <DeleteOutlinedIcon style={{ color: "red" }} />
                </IconButton>
              </div>
            )}
          </div>
          <ListItemText primary={`${w.weight} kg`} />
        </div>
      </ListItem>
    </>
  );
};

export default AddWeightModal;
